import { setLastCoverPosition } from '@redux/discoveryPlaylist';
import { setSelectedVocalEffect } from '@redux/featureFlags';
import {
  configureVolumeMuteState,
  deleteAllSongs,
  disableFeatures,
  saveCurrentSong,
  saveLastSong,
  setVolumeBarDisplay,
} from '@redux/player';
import {
  endCurrentSession,
  resetBamProlongation,
  resetBamSessionUser,
  setBamSessionMode,
  setCustomPlayLists,
} from '@redux/session';
import { isVideoRecordingSelector } from '@redux/session/selectors';
import { BamSessionMode } from '@redux/session/types';
import useSelector from '@redux/useSelector';
import { useDispatch } from 'react-redux';

import { useStartOrStopRecording } from 'components/b2b/Header/camera/camera.utils';
import { VocalEffects } from 'constants/vocalPresets';
import { sendMultiScreenEventToTv } from 'services/helpers/multiScreen';
import { PlayerEvent } from 'shared-types/player-event';
import { useClearGame } from '@redux/game';
import { useBlindTest } from 'hooks/useBlindTest';
import { useVocalGuideState } from './useVocalGuideState';
import analytics from 'lib/analytics';
import { switchInterfaceLanguage } from '@redux/language';
import { selectedVocalEffectSelector } from '@redux/featureFlags/selector';
import { roomCountrySelector } from '@redux/room/selectors';
import { countryToLocale } from 'services/helpers/locale';

export const useClearSession = (): (() => void) => {
  const dispatch = useDispatch();
  const isVideoRecording = useSelector(isVideoRecordingSelector);
  const startOrStopRecordingVideo = useStartOrStopRecording();
  const { clearGame } = useClearGame();
  const { resetBlindTest } = useBlindTest();
  const { deactivateVocalGuide } = useVocalGuideState(null, null);
  const selectedEffect = useSelector(selectedVocalEffectSelector);
  const roomCountry = useSelector(roomCountrySelector);

  const MAX_VOLUME_LEVEL = '100';

  const clearSession = async () => {
    if (isVideoRecording) {
      await startOrStopRecordingVideo();
    }

    if (roomCountry) {
      const locale = countryToLocale(roomCountry);
      dispatch(switchInterfaceLanguage(locale));
      sendMultiScreenEventToTv(PlayerEvent.SWITCH_LANGUAGE, JSON.stringify(locale));
    }

    deactivateVocalGuide();
    sendMultiScreenEventToTv(PlayerEvent.MUTE_VOCAL_GUIDE);
    sendMultiScreenEventToTv(PlayerEvent.PAUSE, null);
    dispatch(setLastCoverPosition(''));
    dispatch(saveCurrentSong(null));
    dispatch(saveLastSong(null));
    dispatch(disableFeatures(false));
    dispatch(deleteAllSongs());
    dispatch(endCurrentSession());
    sendMultiScreenEventToTv(PlayerEvent.SELECT_SONG, null);
    sendMultiScreenEventToTv(PlayerEvent.UPDATE_VOLUME_LEVEL, MAX_VOLUME_LEVEL);
    sendMultiScreenEventToTv(PlayerEvent.STOP_VIDEO, null);
    clearGame();
    dispatch(setVolumeBarDisplay(false));
    dispatch(configureVolumeMuteState(false));
    dispatch(setCustomPlayLists(null));
    dispatch(setBamSessionMode(BamSessionMode.AUTO));
    dispatch(resetBamProlongation());
    dispatch(resetBamSessionUser());
    if (selectedEffect !== VocalEffects.DEFAULT) {
      dispatch(setSelectedVocalEffect(VocalEffects.DEFAULT));
    };

    analytics.stopRecording();

    await resetBlindTest();
  };

  return clearSession;
};
